import { featureFlags } from '@seen/core/featureFlags'
import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import {
  type CriticalAttentionFlagDetails,
  type IsolationRequiredFlagDetails,
  type OosaFlagDetails,
  PatientFlag,
} from '@seen/core/models/flags/patientFlag'
import * as patients from '@seen/core/models/patients'
import type { Participant } from '@seen/core/models/patients/participant'
import { recordValues } from '@seen/core/utils'

import { Badge, Button, cn } from '@seen/ui'
import { IconAlertTriangleFilled, IconFlagPlus } from '@tabler/icons-react'
import { orderBy } from 'lodash-es'
import {
  Fragment,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ComboboxMulti } from '#src/components/core/combobox/ComboboxMulti.tsx'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import {
  applyCriticalAttentionFlagOverlay,
  type CriticalAttentionFlagOverlay,
  criticalAttentionTimelineEventMessage,
  latestCriticalAttentionNotes,
} from '#src/components/critical-attention/criticalAttentionFlag.ts'
import { CriticalAttentionFlagDialog } from '#src/components/critical-attention/CriticalAttentionFlagDialog.tsx'
import { CriticalAttentionFlagHoverCard } from '#src/components/critical-attention/CriticalAttentionFlagHoverCard.tsx'
import {
  applyIsolationRequiredFlagOverlay,
  type IsolationRequiredFlagOverlay,
  isolationRequiredTimelineEventMessage,
  latestIsolationRequiredNotes,
} from '#src/components/isolation-required/isolationRequiredFlag.ts'
import { IsolationRequiredFlagDialog } from '#src/components/isolation-required/IsolationRequiredFlagDialog.tsx'
import { IsolationRequiredFlagHoverCard } from '#src/components/isolation-required/IsolationRequiredFlagHoverCard.tsx'
import {
  applyOosaFlagOverlay,
  latestOosaNotes,
  OOSA_MODAL_OPEN_DELAY_MS,
  type OosaFlagOverlay,
  oosaTimelineEventMessage,
} from '#src/components/oosa/oosaFlag.ts'
import { OosaFlagDialog } from '#src/components/oosa/OosaFlagDialog.tsx'
import { OosaFlagHoverCard } from '#src/components/oosa/OosaFlagHoverCard.tsx'
import { ParticipantFlagBadge } from '#src/components/ParticipantFlagBadge.tsx'
import { sortParticipantFlagsBySeverity } from '#src/components/participantFlagOrder.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useTransactionMutation } from '#src/hooks/useFhirBundleMutation.ts'
import { useParticipantModalFlags } from '#src/hooks/useParticipantModalFlags.ts'
import { useBulkResourceVersions } from '#src/hooks/useResourceVersions.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

type ModalFlagDialogMode = 'create' | 'edit'

type ModalFlagDialogSession =
  | { open: false }
  | { open: true; mode: 'create'; nonce: number }
  | { open: true; mode: 'edit' }

export function ParticipantFlags({
  participant,
  editable,
  variant = 'default',
  className,
  onCriticalIsolationActiveChange,
}: {
  participant: Participant
  editable?: boolean
  // 'header' renders compact flag pills and pushes the add-flag button to
  // the row's end (redesigned facesheet header).
  variant?: 'default' | 'header'
  className?: string
  onCriticalIsolationActiveChange?: (active: {
    criticalAttention: boolean
    isolationRequired: boolean
  }) => void
}): ReactNode {
  const isOosaEnabled = useFeatureFlag(featureFlags.facesheetOosaFlag)
  const isCriticalIsolationEnabled = useFeatureFlag(
    featureFlags.facesheetCriticalIsolationFlags
  )
  useEffect(() => {
    if (!isCriticalIsolationEnabled) {
      onCriticalIsolationActiveChange?.({
        criticalAttention: false,
        isolationRequired: false,
      })
    }
  }, [isCriticalIsolationEnabled, onCriticalIsolationActiveChange])
  if (isOosaEnabled || isCriticalIsolationEnabled) {
    return (
      <OosaAwareParticipantFlags
        participant={participant}
        editable={editable}
        variant={variant}
        className={className}
        isOosaEnabled={isOosaEnabled}
        isCriticalIsolationEnabled={isCriticalIsolationEnabled}
        onCriticalIsolationActiveChange={onCriticalIsolationActiveChange}
      />
    )
  }
  return (
    <ParticipantFlagsView
      participant={participant}
      editable={editable}
      variant={variant}
      className={className}
    />
  )
}

function OosaAwareParticipantFlags({
  participant,
  editable,
  variant = 'default',
  className,
  isOosaEnabled,
  isCriticalIsolationEnabled,
  onCriticalIsolationActiveChange,
}: {
  participant: Participant
  editable?: boolean
  variant?: 'default' | 'header'
  className?: string
  isOosaEnabled: boolean
  isCriticalIsolationEnabled: boolean
  onCriticalIsolationActiveChange?: (active: {
    criticalAttention: boolean
    isolationRequired: boolean
  }) => void
}): ReactNode {
  const practitioner = useCurrentPractitioner()
  const { data: activeFlags } = useParticipantModalFlags(participant.reference)
  // Mode is stored on the same object that opens the dialog so a leftover
  // search Flag cannot mount the form as edit.
  const [dialogSession, setDialogSession] = useState<ModalFlagDialogSession>({
    open: false,
  })
  const [criticalDialogSession, setCriticalDialogSession] =
    useState<ModalFlagDialogSession>({
      open: false,
    })
  const [isolationDialogSession, setIsolationDialogSession] =
    useState<ModalFlagDialogSession>({
      open: false,
    })
  const { data: historicalFlags } = useSearch(
    PatientFlag,
    dialogSession.open ||
      criticalDialogSession.open ||
      isolationDialogSession.open
      ? PatientFlag.byOosa(participant.reference)
      : undefined
  )
  const oosaFlags = historicalFlags ?? activeFlags
  const { mutateAsync: mutateTransaction } = useTransactionMutation()
  const [flagsOpen, setFlagsOpen] = useState(false)
  const dialogModeRef = useRef<ModalFlagDialogMode>('create')
  const criticalDialogModeRef = useRef<ModalFlagDialogMode>('create')
  const isolationDialogModeRef = useRef<ModalFlagDialogMode>('create')
  // Keep this until the next add/remove. FieldForm skips reset while the flags
  // trigger is focused (Radix restores focus there when the modal closes).
  const [oosaOverlay, setOosaOverlay] = useState<OosaFlagOverlay>(null)
  const [criticalOverlay, setCriticalOverlay] =
    useState<CriticalAttentionFlagOverlay>(null)
  const [isolationOverlay, setIsolationOverlay] =
    useState<IsolationRequiredFlagOverlay>(null)
  const [rememberedOosaFlags, setRememberedOosaFlags] = useState<PatientFlag[]>(
    []
  )
  const [rememberedCriticalFlags, setRememberedCriticalFlags] = useState<
    PatientFlag[]
  >([])
  const [rememberedIsolationFlags, setRememberedIsolationFlags] = useState<
    PatientFlag[]
  >([])
  const activeOosaFlags = PatientFlag.activeOosaFlags([
    ...(oosaFlags ?? []),
    ...rememberedOosaFlags,
  ])
  const activeCriticalFlags = PatientFlag.activeCriticalAttentionFlags([
    ...(oosaFlags ?? []),
    ...rememberedCriticalFlags,
  ])
  const activeIsolationFlags = PatientFlag.activeIsolationRequiredFlags([
    ...(oosaFlags ?? []),
    ...rememberedIsolationFlags,
  ])
  // After uncheck + create, search can still return the previous active Flag.
  // null = no current Flag (unchecked or in-flight create). undefined = follow search.
  const [boundOosaId, setBoundOosaId] = useState<string | null>()
  const [boundCriticalId, setBoundCriticalId] = useState<string | null>()
  const [boundIsolationId, setBoundIsolationId] = useState<string | null>()
  const createNonceRef = useRef(0)
  const criticalCreateNonceRef = useRef(0)
  const isolationCreateNonceRef = useRef(0)
  const existingOosa = useMemo(() => {
    if (oosaOverlay === 'remove') {
      return undefined
    }
    if (boundOosaId === null) {
      return undefined
    }
    const pool = [...(oosaFlags ?? []), ...rememberedOosaFlags]
    if (boundOosaId) {
      return (
        pool.find((flag) => flag.toFhirResource().id === boundOosaId) ??
        PatientFlag.findActiveOosa(pool)
      )
    }
    return PatientFlag.findActiveOosa(pool)
  }, [boundOosaId, oosaFlags, oosaOverlay, rememberedOosaFlags])
  const existingCritical = useMemo(() => {
    if (criticalOverlay === 'remove') {
      return undefined
    }
    if (boundCriticalId === null) {
      return undefined
    }
    const pool = [...(oosaFlags ?? []), ...rememberedCriticalFlags]
    if (boundCriticalId) {
      return (
        pool.find((flag) => flag.toFhirResource().id === boundCriticalId) ??
        PatientFlag.findActiveCriticalAttention(pool)
      )
    }
    return PatientFlag.findActiveCriticalAttention(pool)
  }, [boundCriticalId, criticalOverlay, oosaFlags, rememberedCriticalFlags])
  const existingIsolation = useMemo(() => {
    if (isolationOverlay === 'remove') {
      return undefined
    }
    if (boundIsolationId === null) {
      return undefined
    }
    const pool = [...(oosaFlags ?? []), ...rememberedIsolationFlags]
    if (boundIsolationId) {
      return (
        pool.find((flag) => flag.toFhirResource().id === boundIsolationId) ??
        PatientFlag.findActiveIsolationRequired(pool)
      )
    }
    return PatientFlag.findActiveIsolationRequired(pool)
  }, [boundIsolationId, isolationOverlay, oosaFlags, rememberedIsolationFlags])
  const displayedModalFlagCodes = pinDisplayedFlagCodes(
    participant.flagCodes,
    oosaOverlay,
    criticalOverlay,
    isolationOverlay
  )
  const isCriticalAttentionActive = displayedModalFlagCodes.includes(
    patients.ParticipantFlagCode.CRITICAL_ATTENTION
  )
  const isIsolationRequiredActive = displayedModalFlagCodes.includes(
    patients.ParticipantFlagCode.ISOLATION_REQUIRED
  )
  useEffect(() => {
    if (!isCriticalIsolationEnabled) {
      return
    }
    onCriticalIsolationActiveChange?.({
      criticalAttention: isCriticalAttentionActive,
      isolationRequired: isIsolationRequiredActive,
    })
  }, [
    isCriticalAttentionActive,
    isIsolationRequiredActive,
    isCriticalIsolationEnabled,
    onCriticalIsolationActiveChange,
  ])
  const historyFlags = useMemo(() => {
    const byId = new Map<string, PatientFlag>()
    for (const flag of [...(oosaFlags ?? []), ...rememberedOosaFlags]) {
      if (flag.code !== patients.ParticipantFlagCode.OOSA) {
        continue
      }
      const id = flag.toFhirResource().id
      if (id) {
        byId.set(id, flag)
      }
    }
    return [...byId.values()]
  }, [oosaFlags, rememberedOosaFlags])
  const criticalHistoryFlags = useMemo(() => {
    const byId = new Map<string, PatientFlag>()
    for (const flag of [...(oosaFlags ?? []), ...rememberedCriticalFlags]) {
      if (flag.code !== patients.ParticipantFlagCode.CRITICAL_ATTENTION) {
        continue
      }
      const id = flag.toFhirResource().id
      if (id) {
        byId.set(id, flag)
      }
    }
    return [...byId.values()]
  }, [oosaFlags, rememberedCriticalFlags])
  const historyFlagIds = useMemo(
    () =>
      historyFlags
        .map((flag) => flag.toFhirResource().id)
        .filter((id): id is string => id != null),
    [historyFlags]
  )
  const criticalHistoryFlagIds = useMemo(
    () =>
      criticalHistoryFlags
        .map((flag) => flag.toFhirResource().id)
        .filter((id): id is string => id != null),
    [criticalHistoryFlags]
  )
  const isolationHistoryFlags = useMemo(() => {
    const byId = new Map<string, PatientFlag>()
    for (const flag of [...(oosaFlags ?? []), ...rememberedIsolationFlags]) {
      if (flag.code !== patients.ParticipantFlagCode.ISOLATION_REQUIRED) {
        continue
      }
      const id = flag.toFhirResource().id
      if (id) {
        byId.set(id, flag)
      }
    }
    return [...byId.values()]
  }, [oosaFlags, rememberedIsolationFlags])
  const isolationHistoryFlagIds = useMemo(
    () =>
      isolationHistoryFlags
        .map((flag) => flag.toFhirResource().id)
        .filter((id): id is string => id != null),
    [isolationHistoryFlags]
  )
  const [optimisticDetails, setOptimisticDetails] = useState<
    OosaFlagDetails | undefined
  >()
  const [optimisticCriticalDetails, setOptimisticCriticalDetails] = useState<
    CriticalAttentionFlagDetails | undefined
  >()
  const [optimisticIsolationDetails, setOptimisticIsolationDetails] = useState<
    IsolationRequiredFlagDetails | undefined
  >()
  const { data: historyVersions = [] } = useBulkResourceVersions(
    PatientFlag,
    historyFlagIds
  )
  const { data: criticalHistoryVersions = [] } = useBulkResourceVersions(
    PatientFlag,
    criticalHistoryFlagIds
  )
  const { data: isolationHistoryVersions = [] } = useBulkResourceVersions(
    PatientFlag,
    isolationHistoryFlagIds
  )
  const displayDetails = useMemo(() => {
    if (optimisticDetails != null) {
      return optimisticDetails
    }
    const persisted = existingOosa?.oosaDetails
    if (!persisted) {
      return undefined
    }
    const existingOosaId = existingOosa?.toFhirResource().id
    return {
      ...persisted,
      notes:
        persisted.notes ||
        latestOosaNotes(historyVersions, historyFlags, existingOosaId) ||
        '',
    }
  }, [existingOosa, historyFlags, historyVersions, optimisticDetails])
  const displayCriticalDetails = useMemo(() => {
    if (optimisticCriticalDetails != null) {
      return optimisticCriticalDetails
    }
    const persisted = existingCritical?.criticalAttentionDetails
    if (!persisted) {
      return undefined
    }
    const existingCriticalId = existingCritical?.toFhirResource().id
    return {
      ...persisted,
      notes:
        persisted.notes ||
        latestCriticalAttentionNotes(
          criticalHistoryVersions,
          criticalHistoryFlags,
          existingCriticalId
        ) ||
        '',
    }
  }, [
    criticalHistoryFlags,
    criticalHistoryVersions,
    existingCritical,
    optimisticCriticalDetails,
  ])
  const displayIsolationDetails = useMemo(() => {
    if (optimisticIsolationDetails != null) {
      return optimisticIsolationDetails
    }
    const persisted = existingIsolation?.isolationRequiredDetails
    if (!persisted) {
      return undefined
    }
    const existingIsolationId = existingIsolation?.toFhirResource().id
    return {
      ...persisted,
      notes:
        persisted.notes ||
        latestIsolationRequiredNotes(
          isolationHistoryVersions,
          isolationHistoryFlags,
          existingIsolationId
        ) ||
        '',
    }
  }, [
    existingIsolation,
    isolationHistoryFlags,
    isolationHistoryVersions,
    optimisticIsolationDetails,
  ])
  const openTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const criticalOpenTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  )
  const isolationOpenTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  )
  const commitFlagsRef = useRef<
    ((next: patients.ParticipantFlagCode[]) => void) | null
  >(null)
  const pendingFlagsRef = useRef<patients.ParticipantFlagCode[] | null>(null)

  useEffect(
    () => () => {
      if (openTimeoutRef.current) {
        clearTimeout(openTimeoutRef.current)
      }
      if (criticalOpenTimeoutRef.current) {
        clearTimeout(criticalOpenTimeoutRef.current)
      }
      if (isolationOpenTimeoutRef.current) {
        clearTimeout(isolationOpenTimeoutRef.current)
      }
    },
    []
  )

  useEffect(() => {
    if (
      optimisticDetails &&
      existingOosa?.oosaDetails &&
      existingOosa.oosaDetails.startDate === optimisticDetails.startDate &&
      existingOosa.oosaDetails.returnDate === optimisticDetails.returnDate &&
      existingOosa.oosaDetails.returnDateUnknown ===
        optimisticDetails.returnDateUnknown &&
      existingOosa.oosaDetails.reason === optimisticDetails.reason &&
      existingOosa.oosaDetails.otherReason === optimisticDetails.otherReason &&
      existingOosa.oosaDetails.notes === optimisticDetails.notes
    ) {
      setOptimisticDetails(undefined)
    }
  }, [existingOosa?.oosaDetails, optimisticDetails])

  useEffect(() => {
    if (
      optimisticCriticalDetails &&
      existingCritical?.criticalAttentionDetails &&
      existingCritical.criticalAttentionDetails.setOn ===
        optimisticCriticalDetails.setOn &&
      existingCritical.criticalAttentionDetails.reason ===
        optimisticCriticalDetails.reason &&
      existingCritical.criticalAttentionDetails.notes ===
        optimisticCriticalDetails.notes
    ) {
      setOptimisticCriticalDetails(undefined)
    }
  }, [existingCritical?.criticalAttentionDetails, optimisticCriticalDetails])

  useEffect(() => {
    if (
      optimisticIsolationDetails &&
      existingIsolation?.isolationRequiredDetails &&
      existingIsolation.isolationRequiredDetails.setOn ===
        optimisticIsolationDetails.setOn &&
      existingIsolation.isolationRequiredDetails.reason ===
        optimisticIsolationDetails.reason &&
      existingIsolation.isolationRequiredDetails.notes ===
        optimisticIsolationDetails.notes
    ) {
      setOptimisticIsolationDetails(undefined)
    }
  }, [existingIsolation?.isolationRequiredDetails, optimisticIsolationDetails])

  const openOosaDialog = useCallback((mode: ModalFlagDialogMode) => {
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current)
      openTimeoutRef.current = null
    }
    dialogModeRef.current = mode
    setFlagsOpen(false)
    if (mode === 'edit') {
      setDialogSession({ open: true, mode: 'edit' })
      return
    }
    setBoundOosaId(null)
    setOptimisticDetails(undefined)
    createNonceRef.current += 1
    const nextNonce = createNonceRef.current
    openTimeoutRef.current = setTimeout(() => {
      openTimeoutRef.current = null
      if (dialogModeRef.current !== 'create') {
        return
      }
      setDialogSession({ open: true, mode: 'create', nonce: nextNonce })
    }, OOSA_MODAL_OPEN_DELAY_MS)
  }, [])

  const openCriticalDialog = useCallback((mode: ModalFlagDialogMode) => {
    if (criticalOpenTimeoutRef.current) {
      clearTimeout(criticalOpenTimeoutRef.current)
      criticalOpenTimeoutRef.current = null
    }
    criticalDialogModeRef.current = mode
    setFlagsOpen(false)
    if (mode === 'edit') {
      setCriticalDialogSession({ open: true, mode: 'edit' })
      return
    }
    setBoundCriticalId(null)
    setOptimisticCriticalDetails(undefined)
    criticalCreateNonceRef.current += 1
    const nextNonce = criticalCreateNonceRef.current
    criticalOpenTimeoutRef.current = setTimeout(() => {
      criticalOpenTimeoutRef.current = null
      if (criticalDialogModeRef.current !== 'create') {
        return
      }
      setCriticalDialogSession({
        open: true,
        mode: 'create',
        nonce: nextNonce,
      })
    }, OOSA_MODAL_OPEN_DELAY_MS)
  }, [])

  const openIsolationDialog = useCallback((mode: ModalFlagDialogMode) => {
    if (isolationOpenTimeoutRef.current) {
      clearTimeout(isolationOpenTimeoutRef.current)
      isolationOpenTimeoutRef.current = null
    }
    isolationDialogModeRef.current = mode
    setFlagsOpen(false)
    if (mode === 'edit') {
      setIsolationDialogSession({ open: true, mode: 'edit' })
      return
    }
    setBoundIsolationId(null)
    setOptimisticIsolationDetails(undefined)
    isolationCreateNonceRef.current += 1
    const nextNonce = isolationCreateNonceRef.current
    isolationOpenTimeoutRef.current = setTimeout(() => {
      isolationOpenTimeoutRef.current = null
      if (isolationDialogModeRef.current !== 'create') {
        return
      }
      setIsolationDialogSession({
        open: true,
        mode: 'create',
        nonce: nextNonce,
      })
    }, OOSA_MODAL_OPEN_DELAY_MS)
  }, [])

  const handleSelectFlags = useCallback(
    (
      next: patients.ParticipantFlagCode[],
      current: patients.ParticipantFlagCode[],
      commit: (next: patients.ParticipantFlagCode[]) => void
    ) => {
      const addingOosa =
        isOosaEnabled &&
        next.includes(patients.ParticipantFlagCode.OOSA) &&
        !current.includes(patients.ParticipantFlagCode.OOSA)
      const removingOosa =
        isOosaEnabled &&
        current.includes(patients.ParticipantFlagCode.OOSA) &&
        !next.includes(patients.ParticipantFlagCode.OOSA)
      const addingCritical =
        isCriticalIsolationEnabled &&
        next.includes(patients.ParticipantFlagCode.CRITICAL_ATTENTION) &&
        !current.includes(patients.ParticipantFlagCode.CRITICAL_ATTENTION)
      const removingCritical =
        isCriticalIsolationEnabled &&
        current.includes(patients.ParticipantFlagCode.CRITICAL_ATTENTION) &&
        !next.includes(patients.ParticipantFlagCode.CRITICAL_ATTENTION)
      const addingIsolation =
        isCriticalIsolationEnabled &&
        next.includes(patients.ParticipantFlagCode.ISOLATION_REQUIRED) &&
        !current.includes(patients.ParticipantFlagCode.ISOLATION_REQUIRED)
      const removingIsolation =
        isCriticalIsolationEnabled &&
        current.includes(patients.ParticipantFlagCode.ISOLATION_REQUIRED) &&
        !next.includes(patients.ParticipantFlagCode.ISOLATION_REQUIRED)

      if (addingOosa) {
        pendingFlagsRef.current = next
        commitFlagsRef.current = commit
        openOosaDialog('create')
        return
      }

      if (removingOosa) {
        dialogModeRef.current = 'edit'
        setBoundOosaId(null)
        setDialogSession({ open: false })
        setFlagsOpen(false)
        setOosaOverlay('remove')
        setOptimisticDetails(undefined)
        const flagsToInactivate = uniqueOosaFlags(activeOosaFlags)
        setRememberedOosaFlags((prev) =>
          flagsToInactivate.reduce(
            (next, flag) => mergeOosaFlag(next, flag),
            prev
          )
        )
        commit(next)
        void mutateTransaction(
          (builder) => {
            for (const flag of flagsToInactivate) {
              builder.patch(flag, (inactive) => {
                inactive.status = 'inactive'
              })
            }
            builder.create(
              new CommentCommunication({
                partOf: [participant.reference],
                message: oosaTimelineEventMessage(
                  flagsToInactivate[0]?.oosaDetails,
                  'off'
                ),
                sender: practitioner.reference,
              })
            )
          },
          {
            onError: () => {
              setOosaOverlay(null)
              setBoundOosaId(undefined)
              commit(current)
            },
          }
        )
        return
      }

      if (addingCritical) {
        pendingFlagsRef.current = next
        commitFlagsRef.current = commit
        openCriticalDialog('create')
        return
      }

      if (removingCritical) {
        criticalDialogModeRef.current = 'edit'
        setBoundCriticalId(null)
        setCriticalDialogSession({ open: false })
        setFlagsOpen(false)
        setCriticalOverlay('remove')
        setOptimisticCriticalDetails(undefined)
        const flagsToInactivate = uniqueOosaFlags(activeCriticalFlags)
        setRememberedCriticalFlags((prev) =>
          flagsToInactivate.reduce(
            (nextFlags, flag) => mergeOosaFlag(nextFlags, flag),
            prev
          )
        )
        commit(next)
        void mutateTransaction(
          (builder) => {
            for (const flag of flagsToInactivate) {
              builder.patch(flag, (inactive) => {
                inactive.status = 'inactive'
              })
            }
            builder.create(
              new CommentCommunication({
                partOf: [participant.reference],
                message: criticalAttentionTimelineEventMessage(
                  undefined,
                  'off'
                ),
                sender: practitioner.reference,
              })
            )
          },
          {
            onError: () => {
              setCriticalOverlay(null)
              setBoundCriticalId(undefined)
              commit(current)
            },
          }
        )
        return
      }

      if (addingIsolation) {
        pendingFlagsRef.current = next
        commitFlagsRef.current = commit
        openIsolationDialog('create')
        return
      }

      if (removingIsolation) {
        isolationDialogModeRef.current = 'edit'
        setBoundIsolationId(null)
        setIsolationDialogSession({ open: false })
        setFlagsOpen(false)
        setIsolationOverlay('remove')
        setOptimisticIsolationDetails(undefined)
        const flagsToInactivate = uniqueOosaFlags(activeIsolationFlags)
        setRememberedIsolationFlags((prev) =>
          flagsToInactivate.reduce(
            (nextFlags, flag) => mergeOosaFlag(nextFlags, flag),
            prev
          )
        )
        commit(next)
        void mutateTransaction(
          (builder) => {
            for (const flag of flagsToInactivate) {
              builder.patch(flag, (inactive) => {
                inactive.status = 'inactive'
              })
            }
            builder.create(
              new CommentCommunication({
                partOf: [participant.reference],
                message: isolationRequiredTimelineEventMessage(
                  undefined,
                  'off'
                ),
                sender: practitioner.reference,
              })
            )
          },
          {
            onError: () => {
              setIsolationOverlay(null)
              setBoundIsolationId(undefined)
              commit(current)
            },
          }
        )
        return
      }

      commit(next)
    },
    [
      activeCriticalFlags,
      activeIsolationFlags,
      activeOosaFlags,
      mutateTransaction,
      openCriticalDialog,
      isCriticalIsolationEnabled,
      isOosaEnabled,
      openIsolationDialog,
      openOosaDialog,
      participant.reference,
      practitioner.reference,
    ]
  )

  const handleOosaSaved = useCallback(
    (details: OosaFlagDetails) => {
      setOptimisticDetails(details)
      setOosaOverlay('add')
      if (dialogModeRef.current === 'create') {
        setBoundOosaId(null)
      } else {
        const existingId = existingOosa?.toFhirResource().id
        if (existingId) {
          setBoundOosaId(existingId)
        }
      }
      if (pendingFlagsRef.current) {
        commitFlagsRef.current?.(pendingFlagsRef.current)
        pendingFlagsRef.current = null
        commitFlagsRef.current = null
      }
    },
    [existingOosa]
  )

  const handleOosaSaveFailed = useCallback(() => {
    setOosaOverlay(null)
    setOptimisticDetails(undefined)
  }, [])

  const handlePersistedOosaFlag = useCallback((flag: PatientFlag) => {
    setRememberedOosaFlags((prev) => mergeOosaFlag(prev, flag))
    const persistedId = flag.toFhirResource().id
    if (persistedId) {
      setBoundOosaId(persistedId)
    }
  }, [])

  const renderOosaBadge = useCallback(
    ({
      size,
      className: badgeClassName,
    }: {
      size?: 'sm'
      className?: string
    }) => (
      <OosaFlagHoverCard
        details={displayDetails}
        onEdit={editable ? () => openOosaDialog('edit') : undefined}
        size={size}
        className={badgeClassName}
      />
    ),
    [displayDetails, editable, openOosaDialog]
  )

  const handleCriticalSaved = useCallback(
    (details: CriticalAttentionFlagDetails) => {
      setOptimisticCriticalDetails(details)
      setCriticalOverlay('add')
      if (criticalDialogModeRef.current === 'create') {
        setBoundCriticalId(null)
      } else {
        const existingId = existingCritical?.toFhirResource().id
        if (existingId) {
          setBoundCriticalId(existingId)
        }
      }
      if (pendingFlagsRef.current) {
        commitFlagsRef.current?.(pendingFlagsRef.current)
        pendingFlagsRef.current = null
        commitFlagsRef.current = null
      }
    },
    [existingCritical]
  )

  const handleCriticalSaveFailed = useCallback(() => {
    setCriticalOverlay(null)
    setOptimisticCriticalDetails(undefined)
  }, [])

  const handlePersistedCriticalFlag = useCallback((flag: PatientFlag) => {
    setRememberedCriticalFlags((prev) => mergeOosaFlag(prev, flag))
    const persistedId = flag.toFhirResource().id
    if (persistedId) {
      setBoundCriticalId(persistedId)
    }
  }, [])

  const renderCriticalBadge = useCallback(
    ({
      size,
      className: badgeClassName,
    }: {
      size?: 'sm'
      className?: string
    }) => (
      <CriticalAttentionFlagHoverCard
        details={displayCriticalDetails}
        onEdit={editable ? () => openCriticalDialog('edit') : undefined}
        size={size}
        className={badgeClassName}
      />
    ),
    [displayCriticalDetails, editable, openCriticalDialog]
  )

  const handleIsolationSaved = useCallback(
    (details: IsolationRequiredFlagDetails) => {
      setOptimisticIsolationDetails(details)
      setIsolationOverlay('add')
      if (isolationDialogModeRef.current === 'create') {
        setBoundIsolationId(null)
      } else {
        const existingId = existingIsolation?.toFhirResource().id
        if (existingId) {
          setBoundIsolationId(existingId)
        }
      }
      if (pendingFlagsRef.current) {
        commitFlagsRef.current?.(pendingFlagsRef.current)
        pendingFlagsRef.current = null
        commitFlagsRef.current = null
      }
    },
    [existingIsolation]
  )

  const handleIsolationSaveFailed = useCallback(() => {
    setIsolationOverlay(null)
    setOptimisticIsolationDetails(undefined)
  }, [])

  const handlePersistedIsolationFlag = useCallback((flag: PatientFlag) => {
    setRememberedIsolationFlags((prev) => mergeOosaFlag(prev, flag))
    const persistedId = flag.toFhirResource().id
    if (persistedId) {
      setBoundIsolationId(persistedId)
    }
  }, [])

  const renderIsolationBadge = useCallback(
    ({
      size,
      className: badgeClassName,
    }: {
      size?: 'sm'
      className?: string
    }) => (
      <IsolationRequiredFlagHoverCard
        details={displayIsolationDetails}
        onEdit={editable ? () => openIsolationDialog('edit') : undefined}
        size={size}
        className={badgeClassName}
      />
    ),
    [displayIsolationDetails, editable, openIsolationDialog]
  )

  return (
    <>
      <ParticipantFlagsView
        participant={participant}
        editable={editable}
        variant={variant}
        className={className}
        flagsOpen={flagsOpen}
        onFlagsOpenChange={setFlagsOpen}
        onSelectFlags={handleSelectFlags}
        renderOosaBadge={isOosaEnabled ? renderOosaBadge : undefined}
        renderCriticalBadge={
          isCriticalIsolationEnabled ? renderCriticalBadge : undefined
        }
        renderIsolationBadge={
          isCriticalIsolationEnabled ? renderIsolationBadge : undefined
        }
        oosaOverlay={oosaOverlay}
        criticalOverlay={criticalOverlay}
        isolationOverlay={isolationOverlay}
        includeCriticalAttention={isCriticalIsolationEnabled}
      />
      {isOosaEnabled && dialogSession.open && (
        <OosaFlagDialog
          key={
            dialogSession.mode === 'create'
              ? `oosa-create-${dialogSession.nonce}`
              : (existingOosa?.toFhirResource().id ?? 'oosa-edit')
          }
          participant={participant}
          author={{
            ...practitioner.reference,
            display: practitioner.name,
          }}
          open
          mode={dialogSession.mode}
          onOpenChange={(open) => {
            if (!open) {
              setDialogSession({ open: false })
              pendingFlagsRef.current = null
              commitFlagsRef.current = null
            }
          }}
          existingFlag={
            dialogSession.mode === 'create' ? undefined : existingOosa
          }
          flagsToReplace={
            dialogSession.mode === 'create'
              ? uniqueOosaFlags(activeOosaFlags)
              : []
          }
          historyFlagIds={historyFlagIds}
          historyFlags={historyFlags}
          initialDetails={
            dialogSession.mode === 'create'
              ? undefined
              : existingOosa
                ? displayDetails
                : optimisticDetails
          }
          onSaved={handleOosaSaved}
          onPersistedFlag={handlePersistedOosaFlag}
          onSaveFailed={handleOosaSaveFailed}
        />
      )}
      {isCriticalIsolationEnabled && criticalDialogSession.open && (
        <CriticalAttentionFlagDialog
          key={
            criticalDialogSession.mode === 'create'
              ? `critical-create-${criticalDialogSession.nonce}`
              : (existingCritical?.toFhirResource().id ?? 'critical-edit')
          }
          participant={participant}
          author={practitioner.reference}
          open
          mode={criticalDialogSession.mode}
          onOpenChange={(open) => {
            if (!open) {
              setCriticalDialogSession({ open: false })
              pendingFlagsRef.current = null
              commitFlagsRef.current = null
            }
          }}
          existingFlag={
            criticalDialogSession.mode === 'create'
              ? undefined
              : existingCritical
          }
          flagsToReplace={
            criticalDialogSession.mode === 'create'
              ? uniqueOosaFlags(activeCriticalFlags)
              : []
          }
          historyFlagIds={criticalHistoryFlagIds}
          historyFlags={criticalHistoryFlags}
          initialDetails={
            criticalDialogSession.mode === 'create'
              ? undefined
              : existingCritical
                ? displayCriticalDetails
                : optimisticCriticalDetails
          }
          onSaved={handleCriticalSaved}
          onPersistedFlag={handlePersistedCriticalFlag}
          onSaveFailed={handleCriticalSaveFailed}
        />
      )}
      {isCriticalIsolationEnabled && isolationDialogSession.open && (
        <IsolationRequiredFlagDialog
          key={
            isolationDialogSession.mode === 'create'
              ? `isolation-create-${isolationDialogSession.nonce}`
              : (existingIsolation?.toFhirResource().id ?? 'isolation-edit')
          }
          participant={participant}
          author={practitioner.reference}
          open
          mode={isolationDialogSession.mode}
          onOpenChange={(open) => {
            if (!open) {
              setIsolationDialogSession({ open: false })
              pendingFlagsRef.current = null
              commitFlagsRef.current = null
            }
          }}
          existingFlag={
            isolationDialogSession.mode === 'create'
              ? undefined
              : existingIsolation
          }
          flagsToReplace={
            isolationDialogSession.mode === 'create'
              ? uniqueOosaFlags(activeIsolationFlags)
              : []
          }
          historyFlagIds={isolationHistoryFlagIds}
          historyFlags={isolationHistoryFlags}
          initialDetails={
            isolationDialogSession.mode === 'create'
              ? undefined
              : existingIsolation
                ? displayIsolationDetails
                : optimisticIsolationDetails
          }
          onSaved={handleIsolationSaved}
          onPersistedFlag={handlePersistedIsolationFlag}
          onSaveFailed={handleIsolationSaveFailed}
        />
      )}
    </>
  )
}

function ParticipantFlagsView({
  participant,
  editable,
  variant = 'default',
  className,
  flagsOpen,
  onFlagsOpenChange,
  onSelectFlags,
  renderOosaBadge,
  renderCriticalBadge,
  renderIsolationBadge,
  oosaOverlay = null,
  criticalOverlay = null,
  isolationOverlay = null,
  includeCriticalAttention = false,
}: {
  participant: Participant
  editable?: boolean
  variant?: 'default' | 'header'
  className?: string
  flagsOpen?: boolean
  onFlagsOpenChange?: (open: boolean) => void
  onSelectFlags?: (
    next: patients.ParticipantFlagCode[],
    current: patients.ParticipantFlagCode[],
    commit: (next: patients.ParticipantFlagCode[]) => void
  ) => void
  renderOosaBadge?: (props: { size?: 'sm'; className?: string }) => ReactNode
  renderCriticalBadge?: (props: {
    size?: 'sm'
    className?: string
  }) => ReactNode
  renderIsolationBadge?: (props: {
    size?: 'sm'
    className?: string
  }) => ReactNode
  oosaOverlay?: OosaFlagOverlay
  criticalOverlay?: CriticalAttentionFlagOverlay
  isolationOverlay?: IsolationRequiredFlagOverlay
  includeCriticalAttention?: boolean
}): ReactNode {
  const isHeader = variant === 'header'
  // Figma "AlertPill" metrics, now a real Badge size rather than a className
  // override. Pill color stays priority-driven (see ParticipantFlagBadge) so
  // red keeps signalling high-risk flags.
  const badgeSize = isHeader ? 'sm' : undefined
  const options = participantFlagOptions(includeCriticalAttention)
  if (!editable) {
    const flagCodes = pinDisplayedFlagCodes(
      participant.flagCodes,
      oosaOverlay,
      criticalOverlay,
      isolationOverlay
    )
    return (
      // Row and column gaps follow the badge redesign (Figma node 536-1799).
      <div
        className={cn(
          'flex flex-wrap items-center gap-x-1.5 gap-y-2',
          className
        )}
      >
        {flagCodes.map((flagCode) =>
          renderFlagBadge(flagCode, {
            size: badgeSize,
            renderOosaBadge,
            renderCriticalBadge,
            renderIsolationBadge,
          })
        )}
        <StatusBadges participant={participant} badgeSize={badgeSize} />
      </div>
    )
  }

  return (
    <ResourceFieldForm
      instance={participant}
      valueFn={(t) => t.flagCodes}
      patchFn={(t, value) => {
        t.flags = value
      }}
      render={({ field: { value, onChange, ...rest } }) => {
        const displayedFlagCodes = pinDisplayedFlagCodes(
          value,
          oosaOverlay,
          criticalOverlay,
          isolationOverlay
        )
        const isNotShowingFlags =
          displayedFlagCodes.length === 0 &&
          !participant.isDemo &&
          !participant.isOnsite &&
          !participant.isDisenrollmentScheduled &&
          !participant.isDisenrolled

        return (
          <ComboboxMulti
            options={options}
            values={displayedFlagCodes}
            onSelect={(next) => {
              if (onSelectFlags) {
                onSelectFlags(next, displayedFlagCodes, onChange)
                return
              }
              onChange(next)
            }}
            open={flagsOpen}
            onOpenChange={onFlagsOpenChange}
            groupBy={(option) =>
              patients.PARTICIPANT_FLAGS[option.value].category
            }
          >
            <div
              className="flex flex-wrap items-center gap-x-1.5 gap-y-2"
              {...rest}
            >
              {displayedFlagCodes.map((flagCode) =>
                renderFlagBadge(flagCode, {
                  size: badgeSize,
                  className: 'cursor-pointer hover:brightness-95',
                  renderOosaBadge,
                  renderCriticalBadge,
                  renderIsolationBadge,
                })
              )}
              <StatusBadges participant={participant} badgeSize={badgeSize} />
              <Button
                variant="ghost"
                size="unstyled"
                className={cn(
                  'flex items-center gap-1.5 px-1.5',
                  !isNotShowingFlags && 'rounded-full',
                  isHeader && 'ml-auto px-0',
                  // Once flags are present the header add-flag control is a 24px
                  // round ghost icon button (the ghost variant supplies the
                  // hover background); the empty state keeps its natural width
                  // for the "Add flag" label.
                  isHeader && !isNotShowingFlags && 'size-6 justify-center p-0'
                )}
              >
                <div
                  className={cn(
                    'flex items-center justify-center',
                    isHeader ? 'h-5' : 'h-6.5'
                  )}
                >
                  <IconFlagPlus
                    className={cn(
                      'shrink-0',
                      isHeader ? 'size-3' : '-mt-0.5 size-3.5'
                    )}
                  />
                </div>
                {isNotShowingFlags && (
                  <span className="text-xs text-foreground-secondary">
                    Add flag
                  </span>
                )}
              </Button>
            </div>
          </ComboboxMulti>
        )
      }}
    />
  )
}

function StatusBadges({
  participant,
  badgeSize,
}: {
  participant: Participant
  badgeSize?: 'sm'
}): ReactNode {
  return (
    <>
      {participant.isDemo && (
        <Badge variant="outline" size={badgeSize}>
          Demo
        </Badge>
      )}
      {participant.isOnsite && (
        <Badge variant="outline" color="green" size={badgeSize}>
          Onsite
        </Badge>
      )}
      {participant.isDisenrollmentScheduled && (
        <Badge variant="outline" color="yellow" size={badgeSize}>
          Disenrollment Scheduled
        </Badge>
      )}
      {participant.isDisenrolled && (
        <Badge variant="outline" color="red" size={badgeSize}>
          Disenrolled
        </Badge>
      )}
    </>
  )
}

function uniqueOosaFlags(flags: readonly PatientFlag[]): PatientFlag[] {
  const byId = new Map<string, PatientFlag>()
  const withoutId: PatientFlag[] = []
  for (const flag of flags) {
    const id = flag.toFhirResource().id
    if (id) {
      byId.set(id, flag)
    } else {
      withoutId.push(flag)
    }
  }
  return [...byId.values(), ...withoutId]
}

function mergeOosaFlag(
  flags: readonly PatientFlag[],
  next: PatientFlag
): PatientFlag[] {
  const nextId = next.toFhirResource().id
  if (!nextId) {
    return [...flags, next]
  }
  const withoutNext = flags.filter(
    (flag) => flag.toFhirResource().id !== nextId
  )
  return [...withoutNext, next]
}

// Risk flags surface their definitions only on hover of the pill (via the
// badge tooltip), never inline in the dropdown.
const HOVER_ONLY_DESCRIPTION_FLAGS = new Set<patients.ParticipantFlagCode>([
  patients.ParticipantFlagCode.HIGH_RISK,
  patients.ParticipantFlagCode.MEDIUM_RISK,
  patients.ParticipantFlagCode.LOW_RISK,
])

function pinDisplayedFlagCodes(
  flagCodes: readonly patients.ParticipantFlagCode[],
  oosaOverlay: OosaFlagOverlay,
  criticalOverlay: CriticalAttentionFlagOverlay,
  isolationOverlay: IsolationRequiredFlagOverlay = null
): patients.ParticipantFlagCode[] {
  return sortParticipantFlagsBySeverity(
    applyIsolationRequiredFlagOverlay(
      applyCriticalAttentionFlagOverlay(
        applyOosaFlagOverlay(flagCodes, oosaOverlay),
        criticalOverlay
      ),
      isolationOverlay
    )
  )
}

function renderFlagBadge(
  flagCode: patients.ParticipantFlagCode,
  {
    size,
    className,
    renderOosaBadge,
    renderCriticalBadge,
    renderIsolationBadge,
  }: {
    size?: 'sm'
    className?: string
    renderOosaBadge?: (props: { size?: 'sm'; className?: string }) => ReactNode
    renderCriticalBadge?: (props: {
      size?: 'sm'
      className?: string
    }) => ReactNode
    renderIsolationBadge?: (props: {
      size?: 'sm'
      className?: string
    }) => ReactNode
  }
): ReactNode {
  if (flagCode === patients.ParticipantFlagCode.OOSA && renderOosaBadge) {
    return (
      <Fragment key={flagCode}>{renderOosaBadge({ size, className })}</Fragment>
    )
  }
  if (
    flagCode === patients.ParticipantFlagCode.CRITICAL_ATTENTION &&
    renderCriticalBadge
  ) {
    return (
      <Fragment key={flagCode}>
        {renderCriticalBadge({ size, className })}
      </Fragment>
    )
  }
  if (
    flagCode === patients.ParticipantFlagCode.ISOLATION_REQUIRED &&
    renderIsolationBadge
  ) {
    return (
      <Fragment key={flagCode}>
        {renderIsolationBadge({ size, className })}
      </Fragment>
    )
  }
  return (
    <ParticipantFlagBadge
      key={flagCode}
      flag={patients.PARTICIPANT_FLAGS[flagCode]}
      className={className}
      size={size}
    />
  )
}

function participantFlagOptions(includeCriticalAttention: boolean) {
  const modalFlagCodes = new Set<patients.ParticipantFlagCode>([
    patients.ParticipantFlagCode.CRITICAL_ATTENTION,
    patients.ParticipantFlagCode.ISOLATION_REQUIRED,
  ])
  const options = recordValues(patients.PARTICIPANT_FLAGS)
    .filter(({ coding }) => {
      const code = coding.code as patients.ParticipantFlagCode
      if (
        includeCriticalAttention &&
        code === patients.ParticipantFlagCode.ISOLATION_PRECAUTIONS
      ) {
        return false
      }
      return includeCriticalAttention || !modalFlagCodes.has(code)
    })
    .map(({ coding, description }) => {
      const code = coding.code as patients.ParticipantFlagCode
      const showDescriptionInLabel =
        !!description && !HOVER_ONLY_DESCRIPTION_FLAGS.has(code)
      const label = modalFlagCodes.has(code) ? (
        <span
          key={code}
          className="flex w-full items-center justify-between gap-2"
        >
          <span>{coding.display}</span>
          <IconAlertTriangleFilled
            className={cn(
              'size-3',
              code === patients.ParticipantFlagCode.ISOLATION_REQUIRED
                ? 'text-badge-v2-yellow-border'
                : 'text-badge-v2-red-text'
            )}
          />
        </span>
      ) : showDescriptionInLabel ? (
        <span key={code}>
          {coding.display}{' '}
          <span className="text-muted-foreground">({description})</span>
        </span>
      ) : (
        coding.display
      )
      return {
        value: code,
        label,
        keywords: [coding.display, description].filter(Boolean) as string[],
      }
    })
  const pinned = [
    patients.ParticipantFlagCode.CRITICAL_ATTENTION,
    patients.ParticipantFlagCode.ISOLATION_REQUIRED,
  ]
  const leading: typeof options = []
  for (const code of pinned) {
    const index = options.findIndex((option) => option.value === code)
    if (index >= 0) {
      leading.push(...options.splice(index, 1))
    }
  }
  return orderBy(
    [...leading, ...options],
    [
      (option) =>
        patients.PARTICIPANT_FLAG_CATEGORY_ORDER.indexOf(
          patients.PARTICIPANT_FLAGS[option.value].category
        ),
    ],
    ['asc']
  )
}
